import { defaultCtx } from "~/lib/AjaxCtx"
import pick from "lodash/pick"

export default class ProfileService {
    constructor(ctx) {
        this.$http = ctx || defaultCtx
    }

    updatePassword(newPassword, repeatNewPassword, currentPassword) {
        return this.$http.fetchData("/profile/password", {
            method: "PUT",
            body: JSON.stringify({
                newPassword,
                repeatNewPassword,
                currentPassword,
            }),
        })
    }

    /**
     * This request includes validation of required fields
     * @param Object user
     * @returns Object
     */
    async updateProfile(user) {
        return await this.$http.fetchData("/profile", {
            method: "PUT",
            body: JSON.stringify(user),
        })
    }

    /**
     * This request doesn't include validation of required fields
     * it's intended only for changing one field at a time (language, timezone...)
     * @param Object user
     * @returns Object
     */
    async updateProfileAttribute(user) {
        return await this.$http.fetchData("/profile", {
            method: "PATCH",
            body: JSON.stringify(user),
        })
    }

    async updateAvatar(fileHash) {
        return await this.$http.fetchData("/profile/avatar", {
            method: "PUT",
            body: JSON.stringify({
                fileHash,
            }),
        })
    }

    async removeAvatar() {
        return await this.$http.fetchData("/profile/avatar", {
            method: "PUT",
            body: JSON.stringify({
                fileHash: null,
            }),
        })
    }

    async getEditableFields() {
        return await this.$http.fetchData("/profile", { method: "OPTIONS" })
    }

    async getCredits(year) {
        if (!year) {
            throw new Error(
                `Field year is required in order to get the credits`
            )
        }
        return await this.$http.fetchData(`profile/userCredits/${year}`)
    }

    async getPeriod({ year, periodId, cursor }) {
        let url = `/profile/userCredits/${year}/${periodId}`
        if (cursor) {
            url += `?cursor=${cursor}`
        }
        return await this.$http.fetchAPI(url)
    }

    async getResumeData() {
        return await this.$http.fetchData("/profile/experience")
    }

    async getResumeEditFormFields(code) {
        const formFields = await this.$http.fetchData(
            `/profile/experience/${code}`,
            { method: "OPTIONS" }
        )
        return formFields.map(field => {
            const rules = field.rules || []
            if (field.required || rules.some(r => /required_if:/.test(r))) {
                if (field.placeholder)
                    field.placeholder = `*${field.placeholder}`
                if (field.innerLabel) field.innerLabel = `*${field.innerLabel}`
            }
            if (["date", "daterange"].includes(field.type)) {
                field.innerLabel = field.placeholder
            }
            return field
        })
    }

    async editResumeItem(code, formData) {
        if (formData.attachment) {
            formData.attachment = pick(formData.attachment, "name", "fileHash")
        }
        const method = formData.userFormationId ? "PUT" : "POST"
        return await this.$http.fetchData(`profile/experience/${code}`, {
            method,
            body: JSON.stringify(formData),
        })
    }

    async getFormation(query) {
        const url = query ? `/profile/training?${query}` : "/profile/training"
        return await this.$http.fetchAPI(url)
    }

    /**
     * External Training Requests
     */

    async getExternalTrainingRequests({ query, cursor }) {
        let url = "externalFormation/userRequests"
        if (query || cursor) {
            url += "?"
        }
        if (query) {
            url += `${query}`
        }
        if (query && cursor) {
            url += "&"
        }
        if (cursor) {
            url += `cursor=${cursor}`
        }
        return await this.$http.fetchAPI(url)
    }

    async getExternalTrainingRequest(request) {
        const { userRequestId } = request
        return await this.$http.fetchData(
            `externalFormation/userRequests/${userRequestId}`
        )
    }

    async editExternalTrainingRequest(request) {
        const method = request.userRequestId ? "PUT" : "POST"
        return await this.$http.fetchData("externalFormation", {
            method,
            body: JSON.stringify(request),
        })
    }

    async getExternalTrainingFormFields() {
        return await this.$http.fetchData("externalFormation", {
            method: "OPTIONS",
        })
    }

    async getMyDossierData() {
        const { blocks } = await this.$http.fetchData("profile/userDetails")
        return blocks
    }

    async getAttachedResume() {
        return await this.$http.fetchData("profile/userCV")
    }

    async deleteResume() {
        return await this.$http.fetchData("profile/userCV", {
            method: "DELETE",
        })
    }

    async uploadResume(file) {
        file = pick(file, "name", "fileHash", "extension")
        return await this.$http.fetchData("profile/userCV", {
            method: "POST",
            body: JSON.stringify(file),
        })
    }
}
